import {
  PDFDownloadLink,
  Document,
  Page,
  View,
  Image,
  Text,
  Font,
} from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { GOLDPLAY_400_WOFF } from "../utils/fonts.base64";

export const TEST_ID_PRINTABLE_SEASON_CARD_PHYSICAL =
  "printable-season-card-physical";

interface PrintableSeasonCardPhysicalProps {
  name: string;
  photoURL: string;
  qrCodeURL: string;
}

Font.register({ family: "Goldplay", src: GOLDPLAY_400_WOFF });

function mmToPt(mm: number): number {
  const point = 2.83465;
  return mm * point;
}

function getNameFontSize(length: number): string {
  let fontSize: number;
  const base = 24;
  if (length <= base) {
    fontSize = 7;
  } else if (length > base && length < 29) {
    fontSize = 6;
  } else {
    fontSize = 5;
  }
  return `${fontSize}pt`;
}

const PrintableSeasonCardPhysical: React.FC<
  PrintableSeasonCardPhysicalProps
> = ({ name, photoURL, qrCodeURL }) => {
  const { formatMessage } = useIntl();
  const [photo, setPhoto] = useState<string | null>(null);

  const cardWidth = mmToPt(85.7);
  const cardHeight = mmToPt(53.8);
  const photoSize = mmToPt(25);
  const photoTop = mmToPt(10.1);
  const photoLeft = mmToPt(30.1);
  const gap = mmToPt(2.5);
  const qrCodeSize = mmToPt(25);
  const textWidth = mmToPt(27.6);
  const textHeight = mmToPt(5.4);
  const textTop = mmToPt(38);
  const textLeft = mmToPt(29);

  const PDFDocument = (image: string) => (
    <Document>
      <Page size={{ width: cardWidth, height: cardHeight }}>
        <View
          style={{
            position: "absolute",
            width: photoSize,
            height: photoSize,
            top: photoTop,
            left: photoLeft,
          }}
        >
          <Image src={image} />
        </View>
        <View
          style={{
            backgroundColor: "white",
            position: "absolute",
            width: qrCodeSize,
            height: qrCodeSize,
            top: photoTop,
            left: photoLeft + photoSize + gap,
          }}
        >
          <Image src={qrCodeURL} />
        </View>
        <View
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            width: textWidth,
            height: textHeight,
            top: textTop,
            left: textLeft,
          }}
        >
          <Text
            style={{
              fontFamily: "Goldplay",
              fontSize: getNameFontSize(name.length),
            }}
          >
            {name}
          </Text>
        </View>
      </Page>
    </Document>
  );

  useEffect(() => {
    if (typeof window !== "undefined" && !photo) {
      fetch(photoURL)
        .then(response => response.text())
        .then(text => {
          if (text) {
            setPhoto(`data:image/jpeg;base64,${text}`);
          }
        });
    }
  }, [photo, photoURL]);

  return (
    <div
      className="w-100 h-100 fixed top-0 bottom-0 left-0 right-0 flex justify-center sans-serif"
      data-testid={TEST_ID_PRINTABLE_SEASON_CARD_PHYSICAL}
    >
      <div className="mv2">
        {typeof window !== "undefined" && photo && (
          <div className="flex flex-column justify-center">
            <h2 className="f3 mv3 center">Tulostettava kausikortti</h2>
            <p className="block center mv3">
              <strong className="mr3">Nimi</strong>
              <span>{name}</span>
            </p>
            <div className="mv3 flex justify-center">
              <PDFDownloadLink
                document={PDFDocument(photo)}
                fileName="tulostettava-kausikortti.pdf"
                style={{
                  color: "black",
                  border: "2px solid black",
                  borderRadius: "100px",
                  fontFamily: "Trade Gothic Next LT Pro",
                  fontSize: "1rem",
                  fontWeight: 600,
                  padding: "1rem 2rem",
                  textAlign: "center",
                  textTransform: "uppercase",
                  textDecoration: "none",
                }}
              >
                {({ loading }) =>
                  formatMessage({
                    id: loading
                      ? "seasonCardPage.preparingCard"
                      : "seasonCardPage.downloadPDF",
                  })
                }
              </PDFDownloadLink>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintableSeasonCardPhysical;
